import { useSearchParams, useNavigate } from "react-router-dom";
import { useMemo, useCallback } from "react";

/**
 * URL-backed handle for the global Customer 360 drawer.
 *
 * The drawer itself is mounted once in `Layout.jsx` and observes the
 * `?customer=<id>` search param. This hook is the API every trigger point
 * (customer names in tables, order rows, task detail dialogs, …) uses to
 * open / close the drawer without prop-drilling and without a Context.
 *
 * Returns:
 *   • customerId — the currently open customer, or null
 *   • openCustomer(id, opts?) — set the URL param and (optionally) preserve
 *     other search params. `opts.merge = true` merges into current search;
 *     defaults to true so filters on the underlying page survive.
 *   • closeCustomer() — remove the param, preserving other search params.
 */
export function useCustomerDrawer() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const customerId = params.get("customer");

  const openCustomer = useCallback((id, { merge = true } = {}) => {
    if (!id) return;
    if (merge) {
      const next = new URLSearchParams(params);
      next.set("customer", id);
      setParams(next, { replace: false });
    } else {
      navigate(`?customer=${encodeURIComponent(id)}`, { replace: false });
    }
  }, [params, setParams, navigate]);

  const closeCustomer = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("customer");
    setParams(next, { replace: true });
  }, [params, setParams]);

  return useMemo(() => ({ customerId, openCustomer, closeCustomer }),
    [customerId, openCustomer, closeCustomer]);
}
